import { useCallback, useState } from "react";
import { Button, Input, Modal, message } from "antd";
import { CheckOutlined, CloseOutlined, UndoOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { neoActionExecutionController } from "../../AppController";
import { trackNeoEvent } from "./neoAnalytics";
import type {
  NeoActionExecution,
  NeoActionExecutionStatus,
} from "../../hexagon/core/ports/driven/neoActionExecutionRepository";
import styles from "./styles.module.scss";

const STATUS_LABELS: Record<NeoActionExecutionStatus, string> = {
  proposed: "Awaiting approval",
  rejected: "Rejected",
  executing: "Executing",
  completed: "Approved",
  failed: "Failed",
  undoing: "Undoing",
  undone: "Undone",
  undo_failed: "Undo failed",
  cancelled: "Cancelled",
};

export function actionTitle(name: string): string {
  return (name || "")
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function prettyJson(value: Record<string, unknown> | null | undefined): string {
  if (value == null) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** One parameter's value as a line of text. Scalars read plainly; nested
 *  objects and arrays fall back to compact JSON, which is still easier to scan
 *  in a labelled row than inside one wall of pretty-printed text. */
function paramValueText(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return value.trim() === "" ? "(blank)" : value;
  if (typeof value === "number") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** What the change will do, in the order the action declared it. This is the
 *  disclosure the Approve decision is made against, so it is rendered from the
 *  proposal's own parameters rather than summarised. */
const ParamList = ({ params }: { params: Record<string, unknown> }) => {
  const entries = Object.entries(params);
  if (entries.length === 0) {
    return (
      <p className={styles.aeDetailEmpty}>This action takes no parameters.</p>
    );
  }
  return (
    <dl className={styles.aeParamList}>
      {entries.map(([key, value]) => (
        <div key={key} className={styles.aeParamRow}>
          <dt className={styles.aeParamKey}>{actionTitle(key)}</dt>
          <dd className={styles.aeParamValue}>{paramValueText(value)}</dd>
        </div>
      ))}
    </dl>
  );
};

/** The status line under the title. Previously a single sentence rendered for
 *  every status, so a rejected or undone execution still read as if it were
 *  waiting on the user. */
function statusHint(
  status: NeoActionExecutionStatus,
  decisionNote?: string | null
): string {
  switch (status) {
    case "proposed":
      return "Neo proposed a data change that requires your approval before it runs.";
    case "executing":
      return "Applying this change…";
    case "completed":
      return "This change was approved and applied. It can still be undone.";
    case "rejected":
      return decisionNote && decisionNote.trim()
        ? `This change was rejected and never ran — ${decisionNote.trim()}`
        : "This change was rejected and never ran.";
    case "failed":
      return "This change failed to apply. No further action is needed.";
    case "undoing":
      return "Undoing this change…";
    case "undone":
      return "This change was applied and has since been undone.";
    case "undo_failed":
      return "The undo did not complete, so the change is still in effect.";
    case "cancelled":
      return "This proposal was cancelled and never ran.";
    default:
      return "";
  }
}

export interface ActionProposalCardProps {
  executionId: number;
  actionName?: string;
  initialStatus?: NeoActionExecutionStatus;
  clinicId?: number;
  /** The write's parameters — what Approve would actually do.
   *
   *  Supplied by the host, because the card has no way to fetch them: the
   *  approve/reject/undo endpoints are the only ones keyed by execution id, and
   *  they return the payload *after* a decision. Hosts do have it — the chat
   *  page reads the `propose_action` tool call's own input, and the workflow
   *  approval card already holds the loaded execution. Without it the card can
   *  only offer a blind approval, so pass it wherever it is known. */
  params?: Record<string, unknown> | null;
  /** Fired after the user's decision settles the execution (approved,
   *  rejected, or failed to apply). Lets a host surface — e.g. a parked
   *  workflow-step conversation that renders the card standalone — refresh
   *  itself so the resumed step's output flows in. */
  onResolved?: (execution: NeoActionExecution) => void;
  /** Externally force the per-card buttons into a disabled state — used by a
   *  host running a bulk Approve All / Reject All so individual cards can't be
   *  acted on mid-sweep. */
  disabled?: boolean;
}

const ActionProposalCard = ({
  executionId,
  actionName: initialActionName,
  initialStatus = "proposed",
  clinicId,
  params: proposedParams,
  onResolved,
  disabled = false,
}: ActionProposalCardProps) => {
  const [execution, setExecution] = useState<NeoActionExecution | null>(() =>
    initialActionName
      ? {
          id: executionId,
          action_name: initialActionName,
          action_version: "1.0.0",
          status: initialStatus,
          clinic_id: clinicId ?? 0,
          params: proposedParams ?? null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }
      : null
  );
  const [acting, setActing] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [expanded, setExpanded] = useState(false);

  const status = execution?.status ?? initialStatus;
  const actionName = execution?.action_name ?? initialActionName ?? "action";
  const isPending = status === "proposed";
  // A decision response carries the server's own params; before that the
  // host-supplied ones are all there is.
  const paramsObject = execution?.params ?? proposedParams ?? null;

  const applyUpdated = useCallback((updated: NeoActionExecution) => {
    setExecution(updated);
  }, []);

  const handleApprove = useCallback(async () => {
    setActing(true);
    trackNeoEvent("action execution approved", { execution_id: executionId });
    try {
      const res = await neoActionExecutionController.approveActionExecution(
        executionId
      );
      if (res.success && res.data) {
        applyUpdated(res.data);
        setApproveOpen(false);
        if (res.data.status === "completed") {
          message.success("Change approved and applied.");
        } else if (res.data.status === "failed") {
          message.error(res.data.error ?? "The change failed to apply.");
        }
        onResolved?.(res.data);
      } else {
        message.error(res.error?.message ?? "Could not approve the change.");
      }
    } catch (e) {
      message.error(
        e instanceof Error ? e.message : "Could not approve the change."
      );
    } finally {
      setActing(false);
    }
  }, [applyUpdated, executionId, onResolved]);

  const handleReject = useCallback(async () => {
    const reason = rejectReason.trim();
    setActing(true);
    trackNeoEvent("action execution rejected", {
      execution_id: executionId,
      has_reason: reason.length > 0,
    });
    try {
      const res = await neoActionExecutionController.rejectActionExecution(
        executionId,
        reason || undefined
      );
      if (res.success && res.data) {
        applyUpdated(res.data);
        message.success("Change rejected.");
        setRejectOpen(false);
        setRejectReason("");
        onResolved?.(res.data);
      } else {
        message.error(res.error?.message ?? "Could not reject the change.");
      }
    } catch (e) {
      message.error(
        e instanceof Error ? e.message : "Could not reject the change."
      );
    } finally {
      setActing(false);
    }
  }, [applyUpdated, executionId, onResolved, rejectReason]);

  const handleUndo = useCallback(async () => {
    setActing(true);
    trackNeoEvent("action execution undone", { execution_id: executionId });
    try {
      const res = await neoActionExecutionController.undoActionExecution(
        executionId
      );
      if (res.success && res.data) {
        applyUpdated(res.data);
        if (res.data.status === "undone") {
          message.success("Change undone.");
        } else {
          message.error(res.data.error ?? "The undo did not complete.");
        }
      } else {
        message.error(res.error?.message ?? "Could not undo the change.");
      }
    } catch (e) {
      message.error(
        e instanceof Error ? e.message : "Could not undo the change."
      );
    } finally {
      setActing(false);
    }
  }, [applyUpdated, executionId]);

  // Reads from `paramsObject`, so Details is offered *before* a decision. It
  // used to key off the execution's own params, which only arrive in the
  // approve/reject response — the button could not appear until it was too
  // late to inform the decision.
  const params = prettyJson(paramsObject);

  return (
    <div className={classNames(styles.aeCard, styles.actionProposalInline)}>
      <div className={styles.aeCardMain}>
        <div className={styles.aeCardText}>
          <div className={styles.aeCardTitleRow}>
            <span className={styles.aeCardTitle}>
              {actionTitle(actionName)}
            </span>
            <span
              className={classNames(
                styles.aeStatusPill,
                styles[`aeStatus_${status}`]
              )}
            >
              {STATUS_LABELS[status] ?? status}
            </span>
          </div>
          <p className={styles.actionProposalHint}>
            {statusHint(status, execution?.decision_note)}
          </p>
          {execution?.error ? (
            <div className={styles.aeCardError}>{execution.error}</div>
          ) : null}
        </div>
        <div className={styles.aeCardActions}>
          {isPending ? (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={acting}
                disabled={disabled}
                onClick={() => setApproveOpen(true)}
              >
                Approve
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                disabled={acting || disabled}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            </>
          ) : status === "completed" ? (
            <Button
              size="small"
              icon={<UndoOutlined />}
              loading={acting}
              disabled={disabled}
              onClick={() => void handleUndo()}
            >
              Undo
            </Button>
          ) : null}
          {params ? (
            <Button
              type="link"
              size="small"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Hide details" : "Details"}
            </Button>
          ) : null}
        </div>
      </div>
      {expanded && params ? (
        <div className={styles.aeCardDetail}>
          {paramsObject ? (
            <div className={styles.aeDetailBlock}>
              <span className={styles.aeDetailLabel}>What will change</span>
              <ParamList params={paramsObject} />
            </div>
          ) : null}
          <div className={styles.aeDetailBlock}>
            <span className={styles.aeDetailLabel}>Raw parameters</span>
            <pre className={styles.aeDetailJson}>{params}</pre>
          </div>
        </div>
      ) : null}

      {/* Approving is a clinical data write, so it confirms the same way
          rejecting already does — and, unlike before, it shows what the write
          would actually do rather than only its humanized name. */}
      <Modal
        open={approveOpen}
        width={520}
        title={`Approve "${actionTitle(actionName)}"?`}
        okText="Approve and apply"
        okButtonProps={{ loading: acting, disabled }}
        cancelButtonProps={{ disabled: acting }}
        onOk={() => void handleApprove()}
        onCancel={() => {
          if (acting) return;
          setApproveOpen(false);
        }}
      >
        <p className={styles.aeRejectHint}>
          This applies the change to clinical data straight away. It is recorded
          in the audit log and can be undone afterwards.
        </p>
        <div className={styles.aeDetailBlock}>
          <span className={styles.aeDetailLabel}>What will change</span>
          {paramsObject ? (
            <ParamList params={paramsObject} />
          ) : (
            <p className={styles.aeApproveUnknown}>
              The parameters for this change could not be loaded, so there is
              nothing to review here. Open the audit log before approving if you
              need to see exactly what it writes.
            </p>
          )}
        </div>
      </Modal>

      <Modal
        open={rejectOpen}
        title={`Reject "${actionTitle(actionName)}"?`}
        okText="Reject"
        okButtonProps={{ danger: true, loading: acting }}
        cancelButtonProps={{ disabled: acting }}
        onOk={() => void handleReject()}
        onCancel={() => {
          if (acting) return;
          setRejectOpen(false);
          setRejectReason("");
        }}
      >
        <p className={styles.aeRejectHint}>
          The proposed change will not be applied. You can add an optional
          reason for the record.
        </p>
        <Input.TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Reason (optional)"
          maxLength={4000}
          rows={3}
          showCount
        />
      </Modal>
    </div>
  );
};

export function executionIdFromProposeActionOutput(
  output: unknown
): number | null {
  if (!output || typeof output !== "object") return null;
  const o = output as {
    ref?: { id?: number };
    fields?: { id?: number; action_name?: string; status?: string };
  };
  const id = o.ref?.id ?? o.fields?.id;
  return typeof id === "number" && Number.isFinite(id) ? id : null;
}

/** The proposed write's parameters, read from the `propose_action` tool call's
 *  own INPUT rather than its output.
 *
 *  The tool's output deliberately carries only a reference and summary fields,
 *  so the payload the user needs to see before approving is not in it. The
 *  input is the same object the backend validated and stored on the execution
 *  row, and it is already on the client for both live and replayed messages. */
export function proposeActionParamsFromInput(
  input: unknown
): Record<string, unknown> | null {
  if (!input || typeof input !== "object") return null;
  const params = (input as { params?: unknown }).params;
  if (!params || typeof params !== "object" || Array.isArray(params)) {
    return null;
  }
  return params as Record<string, unknown>;
}

export function proposeActionMetaFromOutput(output: unknown): {
  actionName?: string;
  status?: NeoActionExecutionStatus;
  clinicId?: number;
} {
  if (!output || typeof output !== "object") return {};
  const fields = (output as { fields?: Record<string, unknown> }).fields;
  if (!fields) return {};
  const actionName =
    typeof fields.action_name === "string" ? fields.action_name : undefined;
  const status = fields.status as NeoActionExecutionStatus | undefined;
  const clinicId =
    typeof fields.clinic_id === "number" ? fields.clinic_id : undefined;
  return { actionName, status, clinicId };
}

export default ActionProposalCard;
